import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Flame, CalendarClock, CheckCircle2, Tv, ArrowRight } from "lucide-react";
import { TurfTalk, AuthExpiredError } from "@/api/client";
import { GameCard, PageHeader, Empty, Skeleton } from "@/components/ui-kit";

// Preferred durable demo matchup (an NFL game that stays scheduled across days).
const PREFERRED_DEMO_GAME_ID = "game:2a083f0f45805c41"; // GB @ NYJ (Sep 20)

// Featured demo matchup — backend-selected, never hard-coded to a game that
// lacks a ready set of locked picks. Prefers the durable NFL game if valid,
// else the next NFL scheduled game whose 3 character cards are ready.
function FeaturedMatchup() {
  const [game, setGame] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await TurfTalk.games({ league: "NFL", status_filter: "scheduled", limit: 10 });
        const list = r.games || [];
        if (!list.length) { if (alive) setDone(true); return; }
        const ordered = [
          ...list.filter((g) => g.id === PREFERRED_DEMO_GAME_ID),
          ...list.filter((g) => g.id !== PREFERRED_DEMO_GAME_ID),
        ];
        for (const g of ordered.slice(0, 5)) {
          try {
            const p = await TurfTalk.gamePredictions(g.id);
            if ((p.characters || []).length >= 3) { if (alive) { setGame(g); setDone(true); } return; }
          } catch { /* try next */ }
        }
        if (alive) { setGame(ordered[0]); setDone(true); }
      } catch (e) {
        if (alive && !(e instanceof AuthExpiredError)) setDone(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!done) return <Skeleton className="h-28 mb-8" />;
  if (!game) return null;
  const a = game.away || {}, h = game.home || {};
  const kickoff = (() => { try { return new Date(game.start_time).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } })();
  return (
    <Link to={`/game/${game.id}`} data-testid="featured-matchup" className="block mb-9 rounded-2xl overflow-hidden border border-[#2F7D32]/40 bg-gradient-to-r from-[#14361f] to-[#101b28] hover:border-[#2F7D32] transition-colors">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5">
        <div className="flex items-center gap-2 text-[#4ea152]"><Tv size={16} /><span className="font-display tracking-[0.18em] text-[11px]">FEATURED · PREGAME PICKS</span></div>
        <div className="flex items-baseline gap-3 sm:ml-2">
          <span className="font-display text-2xl sm:text-3xl text-white">{a.abbr || "—"} <span className="text-white/30">@</span> {h.abbr || "—"}</span>
          <span className="text-white/45 text-xs">{game.league} · {kickoff}</span>
        </div>
        <span className="sm:ml-auto inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#2F7D32] text-white text-sm font-display tracking-wide" data-testid="featured-cta">
          See the 3 analyst picks <ArrowRight size={16} />
        </span>
      </div>
    </Link>
  );
}

function Row({ icon: Icon, title, games }) {
  if (!games || games.length === 0) return null;
  return (
    <section className="mb-10">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-[#4ea152]" />
        <h2 className="font-display tracking-wide text-lg text-white">{title}</h2>
        <span className="text-white/30 text-sm tabnums">({games.length})</span>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {games.map((g) => <GameCard key={g.id} game={g} />)}
      </div>
    </section>
  );
}

export default function HomePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    TurfTalk.home().then(setData).catch((e) => { if (!(e instanceof AuthExpiredError)) setData({}); }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Happening Now" subtitle={data?.next_action || "Live action, close games, and your upcoming slate."} />
      {loading ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : (
        <>
          <FeaturedMatchup />
          <Row icon={Radio} title="Live Now" games={data?.live} />
          <Row icon={Flame} title="Close Games" games={data?.close_games} />
          <Row icon={CalendarClock} title="Upcoming" games={data?.upcoming} />
          <Row icon={CheckCircle2} title="Recently Final" games={data?.recently_final} />
          {!data?.live?.length && !data?.upcoming?.length && (
            <Empty title="No live action yet" detail="Check the Games tab for the full schedule." icon={Radio} />
          )}
        </>
      )}
    </div>
  );
}
